import HotTable, { HotColumn } from "@handsontable/react";
import { makeStyles } from "@material-ui/core";
import Grid from "@material-ui/core/Grid/Grid";
import Handsontable from "handsontable";
import React, { useEffect, useState } from "react";
import ActionValueTableRow from "../../../api/models/test-series/ActionValueTableRow";
import EnumParameter from "../../../api/models/test-series/EnumParameter";
import EnumParameterValue from "../../../api/models/test-series/EnumParameterValue";
import NumberParameterValue from "../../../api/models/test-series/NumberParameterValue";
import Parameter from "../../../api/models/test-series/Parameter";
import ParameterValue from "../../../api/models/test-series/ParameterValue";
import TextParameterValue from "../../../api/models/test-series/TextParameterValue";
import CustomLinkButton from "../../Button/CustomLinkButton";

interface TableActionsTableProps {
  disabled: boolean;
  resetSelectedActionTemplate: () => void;
  rowParameters: Parameter[] | undefined;
  rows: ActionValueTableRow[] | undefined;
  showErrorSnackbar: (text: string) => void;
}

const useStyles = makeStyles({
  buttonsContainer: {
    "& > * + *": {
      marginLeft: ".5rem",
    },
  },
});

const hotSettings = {
  autoRowSize: false,
};

const TableActionsTable = (props: TableActionsTableProps, externalRef: React.Ref<HotTable>) => {
  const classes = useStyles();
  const { disabled, resetSelectedActionTemplate, rows, rowParameters, showErrorSnackbar } = props;
  const errors = React.useRef<{
    [key: string]: any;
  }>({});
  const [tableColumns, setTableColumns] = useState<React.ReactNode>([]);
  const [tableRows, setTableRows] = useState([] as any[]);

  const getHotValidator = (param: Parameter) => {
    if (param.type === "ENUM") {
      return new RegExp(`^(${(param as EnumParameter).values.join("|")}|)$`);
    }
  };

  const generateHotColumns = (rowParams: Parameter[] | undefined): React.ReactNode => {
    const columnData: any = [];
    rowParams?.forEach((rp: Parameter) => {
      columnData.push({
        data: rp.tmbId,
        key: rp.tmbId,
        source: rp.type === "ENUM" ? (rp as EnumParameter).values : undefined,
        title: rp.name,
        type: rp.type === "NUMBER" ? "numeric" : rp.type === "ENUM" ? "dropdown" : undefined,
        validator: getHotValidator(rp),
      });
      if (rp.type === "NUMBER" && rp.unit) {
        const unitNames = rp.unit.scalings.map((s) => s.code);
        const hasMultipleUnits = unitNames.length > 1;
        columnData.push({
          data: `${rp.tmbId}-unit`,
          key: `${rp.tmbId}-unit`,
          readOnly: !hasMultipleUnits,
          source: unitNames,
          title: "Unit",
          type: hasMultipleUnits ? "dropdown" : undefined,
        });
      }
    });
    return columnData?.map((cd: any) => {
      return (
        <HotColumn
          data={cd.data}
          key={cd.key}
          readOnly={disabled ? true : cd.readOnly}
          source={cd.source}
          title={cd.title}
          type={cd.type}
          validator={cd.validator}
        />
      );
    });
  };

  const generateDefaultRow = (rowParams: Parameter[] | undefined) => {
    const defaultRows = [] as any[];
    const values: { [key: string]: string | number | boolean } = {};
    rowParams?.forEach((p: Parameter) => {
      values[p.tmbId] = "";
      if (p.type === "NUMBER" && p.unit) {
        const unitNames = p.unit.scalings.map((s) => s.code);
        // eslint-disable-next-line prefer-destructuring
        values[`${p.tmbId}-unit`] = unitNames[0];
      }
      if (p.type === "ENUM") {
        // prefill first option
        // eslint-disable-next-line prefer-destructuring
        values[p.tmbId] = (p as EnumParameter).values[0];
      }
    });
    defaultRows.push(values);
    return defaultRows;
  };

  const generateErrors = (allRows: { [key: string]: any }[]): { [key: string]: any }[] => {
    const errorsBase = Object.keys(allRows[0]).reduce((errs, key) => {
      errs[key] = false;
      return errs;
    }, {} as { [key: string]: any });
    return Array(allRows.length).fill(errorsBase);
  };

  const generatePresetRows = (rowValues: ActionValueTableRow[]) => {
    const presetRows = [] as any[];
    rowValues.forEach((r) => {
      const values: { [key: string]: string | number | boolean } = {};
      r.parameterValues.forEach((pv: ParameterValue) => {
        if (pv.type === "NUMBER") {
          values[pv.parameter.tmbId] = (pv as NumberParameterValue).value ?? "";
        }
        if (pv.type === "NUMBER" && (pv as NumberParameterValue).unitCode) {
          values[`${pv.parameter.tmbId}-unit`] = (pv as NumberParameterValue).unitCode || "";
        } else {
          values[pv.parameter.tmbId] = (pv as EnumParameterValue | TextParameterValue).value || "";
        }
      });
      presetRows.push(values);
    });
    return presetRows;
  };

  const transferUnits = (sourceRow: { [key: string]: any }, targetRow: { [key: string]: any }): void => {
    Object.entries(sourceRow).forEach(([key, value]) => {
      if (key.includes("unit")) {
        targetRow[0][key] = value;
      }
    });
  };

  const addTableRow = () => {
    const emptyRow = generateDefaultRow(rowParameters);
    if (tableRows.length) {
      transferUnits(tableRows[tableRows.length - 1], emptyRow);
    }
    const newTableRows = tableRows.concat(emptyRow);
    errors.current = errors.current.concat(generateErrors(emptyRow));
    resetSelectedActionTemplate();
    setTableRows(newTableRows);
  };

  const removeTableRow = () => {
    if (tableRows.length > 1) {
      const newTableRows = tableRows.slice(0, -1);
      errors.current = errors.current.slice(0, -1);
      resetSelectedActionTemplate();
      setTableRows(newTableRows);
    }
  };

  const handleChange = (changes: Handsontable.CellChange[] | null) => {
    const didChange = changes?.some(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_, __, oldVal, newVal]) => newVal !== oldVal,
    );
    if (didChange) {
      resetSelectedActionTemplate();
    }
  };

  const handleValidationResult = (isValid: boolean, value: any, row: number, prop: string | number) => {
    const errorMessage = "Please correct the marked cells.";

    if (!isValid) {
      errors.current[row][prop] = true;
      showErrorSnackbar(errorMessage);
    }
    if (isValid) {
      errors.current[row][prop] = false;

      const allValid = errors.current.every((errorsForRow: { [key: string]: any }) =>
        Object.values(errorsForRow).every((error) => !error),
      );

      if (!allValid) {
        showErrorSnackbar(errorMessage);
      }
    }
  };

  useEffect(() => {
    let rowsForTable: any[] = [];
    if (rowParameters && !rows) {
      rowsForTable = generateDefaultRow(rowParameters);
    }
    if (rowParameters && rows) {
      rowsForTable = generatePresetRows(rows);
    }
    errors.current = generateErrors(rowsForTable);

    setTableRows(rowsForTable);
    setTableColumns(generateHotColumns(rowParameters));
  }, [rows, rowParameters]);

  return (
    <Grid item>
      <HotTable
        afterChange={handleChange}
        afterValidate={handleValidationResult}
        // arbitrary number smaller than container to achieve equal column widths
        colWidths="25"
        data={tableRows}
        height="300"
        id="hot"
        licenseKey="31e17-23a3c-2e0a0-94604-d6938"
        ref={externalRef}
        rowHeights={() => 32}
        settings={hotSettings}
        stretchH="all"
        width="896"
      >
        {tableColumns}
      </HotTable>
      <div className={classes.buttonsContainer}>
        <CustomLinkButton addPlusSign onClick={addTableRow}>
          ADD ROW
        </CustomLinkButton>
        <CustomLinkButton addMinusSign onClick={removeTableRow}>
          REMOVE ROW
        </CustomLinkButton>
      </div>
    </Grid>
  );
};

export default React.forwardRef<HotTable, TableActionsTableProps>(TableActionsTable);
